import { type ApiEnvelope, api, unwrap } from "@/lib/api";

import type {
	BankTransferPending,
	Order,
	OrderEvent,
	OrderNote,
	OrderReturn,
	OrdersAnalyticsStats,
	OrdersFilter,
	OrdersRealtimeStats,
	PaginatedOrders,
} from "./types";

const toQs = (filter: OrdersFilter): string => {
	const usp = new URLSearchParams();
	for (const [k, v] of Object.entries(filter)) {
		if (v === undefined || v === null) continue;
		if (Array.isArray(v)) {
			if (v.length > 0) usp.set(k, v.join(","));
		} else {
			usp.set(k, String(v));
		}
	}
	const qs = usp.toString();
	return qs ? `?${qs}` : "";
};

export const ordersApi = {
	list: (filter: OrdersFilter = {}) =>
		unwrap<PaginatedOrders>(
			api.get<ApiEnvelope<PaginatedOrders>>(`/admin/orders${toQs(filter)}`),
		),

	get: (id: string) =>
		unwrap<Order>(api.get<ApiEnvelope<Order>>(`/admin/orders/${id}`)),

	realtimeStats: () =>
		unwrap<OrdersRealtimeStats>(
			api.get<ApiEnvelope<OrdersRealtimeStats>>("/admin/orders/stats/realtime"),
		),

	analyticsStats: () =>
		unwrap<OrdersAnalyticsStats>(
			api.get<ApiEnvelope<OrdersAnalyticsStats>>(
				"/admin/orders/stats/analytics",
			),
		),

	search: (filter: OrdersFilter) =>
		unwrap<PaginatedOrders>(
			api.post<ApiEnvelope<PaginatedOrders>>("/admin/orders/search", filter),
		),

	createManual: (payload: Record<string, unknown>) =>
		unwrap<Order>(api.post<ApiEnvelope<Order>>("/admin/orders", payload)),

	setStatus: (id: string, status: string, reasonCode?: string) =>
		unwrap<Order>(
			api.post<ApiEnvelope<Order>>(`/admin/orders/${id}/status`, {
				status,
				reasonCode,
			}),
		),

	cancel: (id: string, reasonCode: string, forceApprovalSubmitted = false) =>
		api
			.post<ApiEnvelope<Order>>(`/admin/orders/${id}/cancel`, {
				reasonCode,
				forceApprovalSubmitted,
			})
			.then((r) => r.data),

	forceStatus: (id: string, status: string, reasonCode?: string) =>
		unwrap<Order>(
			api.post<ApiEnvelope<Order>>(`/admin/orders/${id}/force-status`, {
				status,
				reasonCode,
			}),
		),

	archive: (id: string) =>
		unwrap<Order>(api.post<ApiEnvelope<Order>>(`/admin/orders/${id}/archive`)),

	unarchive: (id: string) =>
		unwrap<Order>(
			api.post<ApiEnvelope<Order>>(`/admin/orders/${id}/unarchive`),
		),

	remove: (id: string) => api.delete(`/admin/orders/${id}`).then(() => true),

	addLine: (
		id: string,
		line: {
			variantId: string;
			quantity: number;
			unitPriceOverride?: number;
			discountAmount?: number;
		},
	) =>
		unwrap<Order>(
			api.post<ApiEnvelope<Order>>(`/admin/orders/${id}/lines`, line),
		),

	updateLine: (
		id: string,
		lineId: string,
		patch: { quantity?: number; unitPrice?: number; discountAmount?: number },
	) =>
		unwrap<Order>(
			api.patch<ApiEnvelope<Order>>(
				`/admin/orders/${id}/lines/${lineId}`,
				patch,
			),
		),

	removeLine: (id: string, lineId: string) =>
		api.delete(`/admin/orders/${id}/lines/${lineId}`).then(() => true),

	applyPromo: (id: string, code: string, amount: number) =>
		unwrap<Order>(
			api.post<ApiEnvelope<Order>>(`/admin/orders/${id}/apply-promo`, {
				code,
				amount,
			}),
		),

	bankTransfersPending: () =>
		unwrap<BankTransferPending[]>(
			api.get<ApiEnvelope<BankTransferPending[]>>(
				"/admin/orders/bank-transfers/pending",
			),
		),

	/**
	 * Un montant différent du total fait échouer l'appel en 400 tant que
	 * `acceptGap` n'est pas passé : l'écart doit être reconnu, jamais absorbé.
	 */
	confirmBankTransfer: (
		id: string,
		payload: {
			amount: number;
			valueDate?: string;
			reference?: string;
			acceptGap?: boolean;
		},
	) =>
		unwrap<Order>(
			api.post<ApiEnvelope<Order>>(
				`/admin/orders/${id}/bank-transfer/confirm`,
				payload,
			),
		),

	extendReservation: (id: string, days: number, reason: string) =>
		unwrap<Order>(
			api.post<ApiEnvelope<Order>>(`/admin/orders/${id}/bank-transfer/extend`, {
				days,
				reason,
			}),
		),

	releaseExpiredReservations: () =>
		unwrap<{ liberees: number }>(
			api.post<ApiEnvelope<{ liberees: number }>>(
				"/admin/orders/bank-transfers/release-expired",
			),
		),

	addNote: (id: string, body: string) =>
		unwrap<OrderNote>(
			api.post<ApiEnvelope<OrderNote>>(`/admin/orders/${id}/notes`, { body }),
		),

	listNotes: (id: string) =>
		unwrap<OrderNote[]>(
			api.get<ApiEnvelope<OrderNote[]>>(`/admin/orders/${id}/notes`),
		),

	deleteNote: (id: string, noteId: string) =>
		api.delete(`/admin/orders/${id}/notes/${noteId}`).then(() => true),

	listEvents: (id: string) =>
		unwrap<OrderEvent[]>(
			api.get<ApiEnvelope<OrderEvent[]>>(`/admin/orders/${id}/events`),
		),

	/** Ouvre le PDF dans un nouvel onglet via fetch authentifié (les <a target=_blank> n'envoient pas le Bearer). */
	openPdf: async (id: string, kind: "waybill" | "invoice"): Promise<void> => {
		const res = await api.get(`/admin/orders/${id}/${kind}.pdf`, {
			responseType: "blob",
		});
		const blob = res.data as Blob;
		const url = URL.createObjectURL(blob);
		const w = window.open(url, "_blank");
		if (!w) {
			// Fallback : déclenche un download
			const a = document.createElement("a");
			a.href = url;
			a.download = `${kind}-${id.slice(0, 8)}.pdf`;
			a.click();
		}
		setTimeout(() => URL.revokeObjectURL(url), 60_000);
	},
	waybillPdfUrl: (id: string) =>
		`${api.defaults.baseURL ?? ""}/admin/orders/${id}/waybill.pdf`,
	invoicePdfUrl: (id: string) =>
		`${api.defaults.baseURL ?? ""}/admin/orders/${id}/invoice.pdf`,

	bulkStatus: (ids: string[], status: string, reasonCode?: string) =>
		unwrap<{
			results: Array<{
				id: string;
				ok: boolean;
				status?: string;
				error?: string;
			}>;
		}>(
			api.post<
				ApiEnvelope<{
					results: Array<{
						id: string;
						ok: boolean;
						status?: string;
						error?: string;
					}>;
				}>
			>("/admin/orders/bulk/status", { ids, status, reasonCode }),
		),

	exportCsvUrl: (filter: OrdersFilter = {}) =>
		`${api.defaults.baseURL ?? ""}/admin/orders/export.csv${toQs(filter)}`,
};

export const returnsApi = {
	list: (status?: string) =>
		unwrap<OrderReturn[]>(
			api.get<ApiEnvelope<OrderReturn[]>>(
				`/admin/returns${status ? `?status=${status}` : ""}`,
			),
		),

	get: (id: string) =>
		unwrap<OrderReturn>(
			api.get<ApiEnvelope<OrderReturn>>(`/admin/returns/${id}`),
		),

	create: (
		orderId: string,
		payload: {
			reasonCode: string;
			customerNote?: string;
			lines: Array<{ orderLineId: string; quantity: number }>;
		},
	) =>
		unwrap<OrderReturn>(
			api.post<ApiEnvelope<OrderReturn>>(
				`/admin/orders/${orderId}/returns`,
				payload,
			),
		),

	accept: (id: string) =>
		unwrap<OrderReturn>(
			api.post<ApiEnvelope<OrderReturn>>(`/admin/returns/${id}/accept`, {}),
		),
	reject: (id: string, reason?: string) =>
		unwrap<OrderReturn>(
			api.post<ApiEnvelope<OrderReturn>>(`/admin/returns/${id}/reject`, {
				reason,
			}),
		),
	receive: (id: string) =>
		unwrap<OrderReturn>(
			api.post<ApiEnvelope<OrderReturn>>(`/admin/returns/${id}/receive`, {}),
		),
	inspect: (
		id: string,
		payload: {
			decision: "FULL_REFUND" | "PARTIAL_REFUND" | "REFUSED";
			inspectionNote?: string;
			partialAmount?: number;
		},
	) =>
		unwrap<OrderReturn>(
			api.post<ApiEnvelope<OrderReturn>>(
				`/admin/returns/${id}/inspect`,
				payload,
			),
		),
};

export const refundsApi = {
	list: (orderId?: string) =>
		unwrap<
			Array<{
				id: string;
				number: string;
				orderId: string;
				amount: number;
				status: string;
				createdAt: string;
			}>
		>(api.get(`/admin/refunds${orderId ? `?orderId=${orderId}` : ""}`)),
	create: (
		orderId: string,
		amount: number,
		returnId?: string,
		reason?: string,
	) =>
		api
			.post(`/admin/orders/${orderId}/refunds`, { amount, returnId, reason })
			.then((r) => r.data),
	execute: (id: string, externalReference: string) =>
		api
			.post(`/admin/refunds/${id}/execute`, { externalReference })
			.then((r) => r.data),
};
